import { Injectable, inject } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import type { FormLinkListPagination } from './form-short-url.service';

export interface FormQrCodeCreateResponse {
  id: string;
  formShortUrlId: string;
  shortUrl: string;
}

export interface FormQrCodeListItem {
  id: string;
  formShortUrlId: string;
  shortUrl: string;
  label?: string | null;
  enabled: boolean;
  createdAt: string | null;
}

export type FormQrCodeListResponse = {
  items: FormQrCodeListItem[];
  pagination: FormLinkListPagination;
};

@Injectable({
  providedIn: 'root',
})
export class FormQrCodeService {
  private api = inject(ApiService);
  private baseUrl = '';

  setBaseUrl(url: string): void {
    this.baseUrl = url;
  }

  listQrCodes(
    workspaceId: string,
    sessionId: string,
    formId: string,
    options?: { limit?: number; cursor?: string },
  ): Observable<FormQrCodeListResponse> {
    const params: Record<string, string | number | boolean> = {
      workspaceId,
      sessionId,
      formId,
    };
    if (options?.limit != null) {
      params['limit'] = options.limit;
    }
    if (options?.cursor) {
      params['cursor'] = options.cursor;
    }
    return from(
      this.api.get<FormQrCodeListResponse>(
        this.baseUrl,
        '/form_qr_codes_api/api/form-qr-codes',
        params,
      ),
    ).pipe(switchMap((obs) => obs));
  }

  async createQrCode(
    workspaceId: string,
    sessionId: string,
    formId: string,
    label?: string | null,
  ): Promise<FormQrCodeCreateResponse> {
    const body = {
      workspaceId,
      sessionId,
      formId,
      ...(label?.trim() ? { label: label.trim() } : {}),
    };
    const obs = await this.api.post<FormQrCodeCreateResponse, typeof body>(
      this.baseUrl,
      '/form_qr_codes_api/api/form-qr-codes',
      body,
    );
    const result = await firstValueFrom(obs);
    if (!result?.id || !result?.formShortUrlId || !result?.shortUrl) {
      throw new Error('Invalid response from create QR code');
    }
    return result;
  }

  async updateEnabled(id: string, enabled: boolean): Promise<void> {
    const obs = await this.api.patch<unknown, { enabled: boolean }>(
      this.baseUrl,
      `/form_qr_codes_api/api/form-qr-codes/${id}`,
      { enabled },
    );
    await firstValueFrom(obs);
  }

  async updateLabel(id: string, label: string | null): Promise<void> {
    const obs = await this.api.patch<unknown, { label: string }>(
      this.baseUrl,
      `/form_qr_codes_api/api/form-qr-codes/${id}`,
      { label: label?.trim() ?? '' },
    );
    await firstValueFrom(obs);
  }
}
